/* eslint-disable no-param-reassign */
import { cloneDeep, get, set, unset, isEqual } from 'lodash';
import { getOptionsAsync } from '../../triggerApi';
import findByKey from '../../../objectManipulation/findByKey';
import {
  expandSourcePath,
  getDynamicDataState,
  hasUnresolvedParams,
  resolveValueParams,
} from '../../iteration';

/**
 * Liste les instances à traiter : une par champ dynamique hors collection, et une
 * par itération existante pour un champ dans une collection ("@source" avec [*]).
 *
 * @param {array} dynamicDatas - Les configs "@dynamicData"
 * @param {object} formdata - Les valeurs actuelles du formulaire
 * @returns {array} Liste de { dynamicData, path, indexes }
 */
const listInstances = (dynamicDatas, formdata) =>
  dynamicDatas.flatMap((dynamicData) =>
    expandSourcePath(dynamicData['@source'], formdata).map(
      ({ path, indexes }) => ({ dynamicData, path, indexes })
    )
  );

/**
 * Supprime l'état des itérations qui n'existent plus (élément retiré d'une collection).
 *
 * @param {array} dynamicDatas - Les configs "@dynamicData" (mutées)
 * @param {array} instances - Les instances existantes (listInstances)
 */
const cleanRemovedIterations = (dynamicDatas, instances) => {
  dynamicDatas.forEach((dynamicData) => {
    if (dynamicData['@byPath']) {
      const existingPaths = instances
        .filter((instance) => instance.dynamicData === dynamicData)
        .map((instance) => instance.path);
      Object.keys(dynamicData['@byPath']).forEach((path) => {
        if (!existingPaths.includes(path)) {
          delete dynamicData['@byPath'][path];
        }
      });
    }
  });
};

/**
 * Vide la valeur des champs dynamiques dont les paramètres dépendants ont changé
 * depuis le dernier passage (ex : le Sous-thème quand le Thème change), pour ne
 * pas garder une valeur qui ne fait plus partie des options disponibles.
 * Fonctionne en cascade (Thème → Sous-thème → Sous-sous-thème) et itération par
 * itération dans les collections.
 *
 * Les derniers paramètres vus sont mémorisés dans "@paramsAtChange" : au premier
 * passage (chargement du formulaire) ils sont seulement enregistrés, pour ne pas
 * effacer les données déjà saisies. On ne vide pas non plus quand la source
 * n'avait pas encore de valeur (cas d'un formData fourni après le montage) :
 * le champ dépendant ne pouvait alors pas avoir été choisi par l'utilisateur.
 *
 * Dans une collection, si la valeur du champ a changé en même temps que ses
 * paramètres, c'est que l'élément a été déplacé ou qu'un élément précédent a été
 * supprimé (les index se décalent) : la valeur suit son élément, on ne vide pas.
 *
 * @param {array} dynamicDatas - Les configs "@dynamicData" à surveiller (mutées)
 * @param {object} formdata - Le formdata à nettoyer (muté)
 * @returns {boolean} true si au moins un champ a été vidé
 */
const resetOutdatedDependentValues = (dynamicDatas, formdata) => {
  let resetDone = false;
  let hasChanged = true;

  while (hasChanged) {
    hasChanged = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const { dynamicData, path, indexes } of listInstances(
      dynamicDatas,
      formdata
    )) {
      const state = getDynamicDataState(dynamicData, path, true);
      const currentParams = resolveValueParams(
        dynamicData.params,
        formdata,
        indexes
      );
      const currentValue = path ? get(formdata, path) : undefined;
      const lastParams = state['@paramsAtChange'];
      const movedWithItem =
        indexes.length > 0 && !isEqual(state['@valueAtChange'], currentValue);

      if (
        lastParams &&
        !hasUnresolvedParams(lastParams) &&
        !isEqual(lastParams, currentParams) &&
        path &&
        currentValue !== undefined &&
        !movedWithItem
      ) {
        unset(formdata, path);
        resetDone = true;
        // un champ vidé peut lui-même être la source d'un autre champ
        hasChanged = true;
      }

      state['@paramsAtChange'] = currentParams;
      state['@valueAtChange'] = path ? get(formdata, path) : undefined;
    }
  }

  return resetDone;
};

/**
 * Extension qui gère le rechargement dynamique des champs dépendants.
 * Appelée à l'initialisation et à chaque changement de valeur dans le formulaire.
 *
 * @param {object} formdata - Les valeurs actuelles de tous les champs du formulaire
 * @param {object} currentJSSchema - Le schéma JSON du formulaire (structure des champs)
 * @param {object} _jsonSchema - Le schéma JSON original (non utilisé ici)
 * @param {function} setCurrentJSchema - Met à jour le schéma (pour déclencher un re-render)
 * @param {function} setCurrentFormAtom - Met à jour l'atom Recoil partagé du formulaire
 * @param {function} setFormData - Met à jour les valeurs du formulaire
 */
const extensionDynamicData = async (
  formdata,
  currentJSSchema,
  _jsonSchema,
  setCurrentJSchema,
  setCurrentFormAtom,
  setFormData
) => {
  // Recherche tous les champs du schéma qui ont une config "@dynamicData"
  // (= champs dont les options sont chargées dynamiquement via API)
  const dynamicDatas = findByKey(currentJSSchema, '@dynamicData');

  // Copie profonde du formdata pour ne pas muter l'original
  let newFormdata = cloneDeep(formdata);

  // Ne traite que les champs qui ont des paramètres dépendants d'autres champs
  // (@dependentParams) ou qui doivent mettre à jour le formdata (actOnFormdata)
  const dependentDynamicDatas = dynamicDatas.filter(
    (dynamicData) =>
      dynamicData?.['@dependentParams'] || dynamicData?.actOnFormdata
  );

  // Vide les champs dont la source a changé, et l'applique tout de suite
  // (avant les appels API) pour que le formulaire ne garde pas l'ancienne valeur
  if (resetOutdatedDependentValues(dependentDynamicDatas, newFormdata)) {
    setFormData(cloneDeep(newFormdata));
  }
  const formdataAfterReset = cloneDeep(newFormdata);

  const instances = listInstances(dependentDynamicDatas, formdataAfterReset);
  cleanRemovedIterations(dependentDynamicDatas, instances);

  // Traite tous les champs dynamiques (et toutes les itérations) en parallèle
  await Promise.all(
    instances.map(async ({ dynamicData, path, indexes }) => {
      const state = getDynamicDataState(dynamicData, path, true);
      const parsedParams = resolveValueParams(
        dynamicData.params,
        formdataAfterReset,
        indexes
      );

      // Ne refait l'appel API que si les paramètres ont changé depuis le dernier
      // appel (terminé ou en cours), pour éviter les appels inutiles
      const alreadyRequested =
        state['@isFetching'] &&
        isEqual(state['@paramsRequested'], parsedParams);
      if (
        !alreadyRequested &&
        (!state['@paramsAtFetch'] ||
          !isEqual(state['@paramsAtFetch'], parsedParams))
      ) {
        state['@isFetching'] = true;
        state['@paramsRequested'] = parsedParams;
        await setCurrentJSchema(currentJSSchema);

        // Notifie les composants que les données dynamiques vont être mises à jour
        if (!dynamicData.actOnFormdata) {
          setCurrentFormAtom((prev) => ({
            ...prev,
            dynamicData: {
              ...prev.dynamicData,
              updatedTimestamp: Date.now(),
            },
          }));
        }

        // Appel API pour récupérer les nouvelles options
        await getOptionsAsync({ ...dynamicData, params: parsedParams }).then(
          async (filteredOptions) => {
            // Ignore une réponse obsolète (un appel plus récent a été lancé)
            if (!isEqual(state['@paramsRequested'], parsedParams)) {
              return;
            }
            state['@isFetching'] = false;
            // Stocke les options récupérées (dans le schéma ou par itération)
            state['@fetched'] = filteredOptions;
            // Mémorise les paramètres utilisés pour cet appel
            state['@paramsAtFetch'] = parsedParams;

            // Si le champ est configuré pour mettre à jour directement le formdata
            // (actOnFormdata = true), on écrit automatiquement la première option
            // dans le formdata — c'est le cas du champ dynamique non modifiable
            if (
              filteredOptions?.[0]?.label &&
              dynamicData?.actOnFormdata === true &&
              path
            ) {
              // Met à jour le formdata avec le libellé de la première option retournée
              // Note: on écrase toujours la valeur existante pour refléter
              // le changement du champ source
              newFormdata = set(newFormdata, path, filteredOptions[0].label);
            }
          }
        );
        await setCurrentJSchema(currentJSSchema);
      }

      // Notifie les composants que la mise à jour est terminée
      if (!dynamicData.actOnFormdata) {
        setCurrentFormAtom((prev) => ({
          ...prev,
          dynamicData: {
            ...prev.dynamicData,
            updatedTimestamp: Date.now(),
          },
        }));
      }
      return null;
    })
  );

  // Ne met à jour le formdata que si les appels API l'ont modifié (actOnFormdata) ;
  // la remise à zéro des champs dépendants a déjà été appliquée plus haut
  if (!isEqual(formdataAfterReset, newFormdata)) {
    setFormData(newFormdata);
  }
};

export default extensionDynamicData;
